import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, CheckCircle, Copy, CreditCard, PlugsConnected, Warning } from "@phosphor-icons/react";
import { AppHeader } from "@/components/AppHeader";
import { SecretsForm } from "@/components/SecretsForm";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { api, formatApiErrorDetail } from "@/lib/api";

const Pill = ({ ok, yes = "Configured", no = "Not configured", testid }) => (
  <span
    data-testid={testid}
    className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest ${
      ok ? "bg-emerald-500/15 text-emerald-300" : "bg-amber-500/15 text-amber-300"
    }`}
  >
    {ok ? <CheckCircle size={12} weight="bold" /> : <Warning size={12} weight="bold" />}
    {ok ? yes : no}
  </span>
);

const UrlRow = ({ label, url, testid }) => (
  <div className="flex flex-wrap items-center gap-2 text-xs">
    <span className="w-28 shrink-0 text-[10px] font-bold uppercase tracking-widest text-zinc-500">
      {label}
    </span>
    <code className="font-mono-data min-w-0 flex-1 truncate rounded bg-zinc-950 px-2 py-1 text-zinc-300" data-testid={testid}>
      {url}
    </code>
    <button
      type="button"
      aria-label={`Copy ${label}`}
      data-testid={`${testid}-copy`}
      onClick={() => {
        navigator.clipboard?.writeText(url);
        toast.success("Copied");
      }}
      className="rounded-md border border-zinc-800 p-1.5 text-zinc-400 hover:bg-zinc-800"
    >
      <Copy size={13} weight="bold" />
    </button>
  </div>
);

const Section = ({ title, sub, right, children, testid }) => (
  <section className="rounded-lg border border-zinc-800 bg-zinc-900 p-5" data-testid={testid}>
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div>
        <h2 className="text-xs font-bold uppercase tracking-widest text-zinc-300">{title}</h2>
        <p className="mt-1 text-xs text-zinc-500">{sub}</p>
      </div>
      {right}
    </div>
    <div className="mt-4 space-y-3">{children}</div>
  </section>
);

export default function PaymentsSettings() {
  const navigate = useNavigate();
  const [conf, setConf] = useState(null);
  const [busy, setBusy] = useState(false);
  const [payaTest, setPayaTest] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/settings/payments");
      setConf(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not load");
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const saveSecrets = async (values) => {
    setBusy(true);
    try {
      const { data } = await api.put("/settings/payments/secrets", { values });
      setConf(data);
      setPayaTest(null);
      toast.success("Secrets saved");
      setBusy(false);
      return true;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not save");
      setBusy(false);
      return false;
    }
  };

  const testPaya = async () => {
    setBusy(true);
    setPayaTest(null);
    try {
      const { data } = await api.post("/settings/payments/paya/test");
      setPayaTest(data);
      if (data.ok) toast.success("Paya connection works");
      else toast.error("Paya test failed");
    } catch (err) {
      setPayaTest({ ok: false, error: formatApiErrorDetail(err.response?.data?.detail) || "Test failed" });
    }
    setBusy(false);
  };

  const save = async (patch) => {
    const next = { ...conf, ...patch };
    setConf(next);
    setBusy(true);
    try {
      const { data } = await api.put("/settings/payments", {
        stripe_enabled: next.stripe_enabled,
        quickbooks_enabled: next.quickbooks_enabled,
        paya_enabled: next.paya_enabled,
        paya_mode: next.paya_mode,
      });
      setConf(data);
      toast.success("Saved");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not save");
      load();
    }
    setBusy(false);
  };

  if (!conf) return null;
  const p = conf.providers;

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100">
      <AppHeader />
      <main className="mx-auto max-w-4xl px-4 py-8" data-testid="payments-settings-page">
        <button
          type="button"
          data-testid="payments-settings-back"
          onClick={() => navigate("/settings")}
          className="mb-4 inline-flex items-center gap-1 text-xs text-zinc-500 hover:text-zinc-300"
        >
          <ArrowLeft size={13} weight="bold" /> Settings
        </button>
        <div className="flex items-center gap-3">
          <CreditCard size={26} weight="bold" className="text-emerald-400" />
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Payments</h1>
            <p className="text-sm text-zinc-500">
              Which ways customers can pay an invoice in the portal. Credentials are saved
              encrypted on the server (or set as backend environment secrets, which take
              priority) — nothing here is ever sent to the browser.
            </p>
          </div>
        </div>

        <div className="mt-8 space-y-6">
          <Section
            testid="payments-stripe"
            title="Card checkout (Stripe)"
            sub="Hosted Stripe checkout page. Mode and keys come from STRIPE_SECRET_KEY / STRIPE_MODE."
            right={
              <div className="flex items-center gap-3">
                <Pill ok={p.stripe.configured} testid="payments-stripe-status" />
                <Switch
                  data-testid="payments-stripe-toggle"
                  checked={conf.stripe_enabled}
                  disabled={busy}
                  onCheckedChange={(v) => save({ stripe_enabled: v })}
                />
              </div>
            }
          >
            <UrlRow label="Webhook" url={p.stripe.webhook_url} testid="payments-stripe-webhook" />
            <p className="text-xs text-zinc-500">Mode: {p.stripe.mode}</p>
          </Section>

          <Section
            testid="payments-quickbooks"
            title="QuickBooks Payments"
            sub="Shows the QuickBooks-hosted pay page (card or bank) for every synced invoice, and marks the invoice paid when QuickBooks tells us."
            right={
              <div className="flex items-center gap-3">
                <Pill
                  ok={p.quickbooks.configured}
                  yes="Connected"
                  no="Not connected"
                  testid="payments-quickbooks-status"
                />
                <Switch
                  data-testid="payments-quickbooks-toggle"
                  checked={conf.quickbooks_enabled}
                  disabled={busy}
                  onCheckedChange={(v) => save({ quickbooks_enabled: v })}
                />
              </div>
            }
          >
            <UrlRow label="Webhook" url={p.quickbooks.webhook_url} testid="payments-quickbooks-webhook" />
            <p className="text-xs text-zinc-500">
              Register the webhook URL in the Intuit developer dashboard (Webhooks → Invoice + Payment
              events) and paste its Verifier Token below.{" "}
              <Pill
                ok={p.quickbooks.verifier_set}
                yes="Verifier set"
                no="Verifier token missing"
                testid="payments-quickbooks-verifier"
              />
            </p>
            <SecretsForm
              testid="payments-quickbooks-secrets"
              keys={["INTUIT_WEBHOOK_VERIFIER_TOKEN"]}
              secrets={conf.secrets}
              busy={busy}
              onSave={saveSecrets}
            />
            <p className="text-xs text-zinc-500" data-testid="payments-quickbooks-webhook-last">
              Last webhook from Intuit:{" "}
              {p.quickbooks.webhook_last?.at
                ? `${new Date(p.quickbooks.webhook_last.at).toLocaleString()} · ${
                    p.quickbooks.webhook_last.ok
                      ? `signature OK · ${p.quickbooks.webhook_last.events} event(s)`
                      : p.quickbooks.webhook_last.error || "rejected"
                  }`
                : "none yet"}
            </p>
            <p className="text-xs text-zinc-500">
              Turn on QuickBooks Payments inside QuickBooks Online (Sales → Online payments) so the
              hosted page accepts cards and ACH. Staff can also press “Check QuickBooks for payments”
              on any invoice.
            </p>
          </Section>

          <Section
            testid="payments-paya"
            title="Nuvei · Paya Connect"
            sub={`Card sale through the Paya Connect API. ${p.paya.sandbox ? "Sandbox" : "Production"} · ${p.paya.base_url}`}
            right={
              <div className="flex items-center gap-3">
                <Pill ok={p.paya.configured} testid="payments-paya-status" />
                <Switch
                  data-testid="payments-paya-toggle"
                  checked={conf.paya_enabled}
                  disabled={busy || !p.paya.configured}
                  onCheckedChange={(v) => save({ paya_enabled: v })}
                />
              </div>
            }
          >
            <div>
              <span className="mb-1 block text-[10px] font-bold uppercase tracking-widest text-zinc-500">
                Card entry
              </span>
              <select
                data-testid="payments-paya-mode"
                value={conf.paya_mode}
                disabled={busy}
                onChange={(e) => save({ paya_mode: e.target.value })}
                className="w-full max-w-sm rounded-lg border border-zinc-800 bg-zinc-950 px-3 py-2 text-sm text-zinc-100 outline-none focus:border-indigo-400/50"
              >
                <option value="direct">On our portal — charged server-side (backend sale call)</option>
                <option value="hosted" disabled={!p.paya.hash_key_set}>
                  Paya hosted PayForm (needs the User hash key)
                </option>
              </select>
            </div>
            <UrlRow label="Postback" url={p.paya.postback_url} testid="payments-paya-postback" />
            <UrlRow label="Return URL" url={p.paya.return_url} testid="payments-paya-return" />
            <p className="text-xs text-zinc-500">
              Register the postback URL under Location → Settings → Postback Config (resource:
              transaction, on create + update). Optional Basic Auth user / password below.{" "}
              <Pill
                ok={p.paya.postback_auth_set}
                yes="Postback auth on"
                no="Postback auth off"
                testid="payments-paya-postback-auth"
              />
            </p>
            <div>
              <span className="mb-1 block text-[10px] font-bold uppercase tracking-widest text-zinc-500">
                Environment
              </span>
              <select
                data-testid="payments-paya-env"
                value={p.paya.sandbox ? "sandbox" : "production"}
                disabled={busy || p.paya.base_url_locked}
                onChange={(e) =>
                  saveSecrets({
                    PAYA_BASE_URL: e.target.value === "sandbox" ? p.paya.sandbox_url : p.paya.production_url,
                  })
                }
                className="w-full max-w-sm rounded-lg border border-zinc-800 bg-zinc-950 px-3 py-2 text-sm text-zinc-100 outline-none focus:border-indigo-400/50 disabled:opacity-50"
              >
                <option value="sandbox">Sandbox · {p.paya.sandbox_url}</option>
                <option value="production">Production · {p.paya.production_url}</option>
              </select>
              {p.paya.base_url_locked && (
                <p className="mt-1 text-[11px] text-zinc-500">
                  PAYA_BASE_URL is set in the backend environment — change it there.
                </p>
              )}
            </div>
            <SecretsForm
              testid="payments-paya-secrets"
              keys={[
                "PAYA_DEVELOPER_ID",
                "PAYA_USER_ID",
                "PAYA_USER_API_KEY",
                "PAYA_LOCATION_ID",
                "PAYA_PRODUCT_TRANSACTION_ID",
                "PAYA_USER_HASH_KEY",
                "PAYA_POSTBACK_BASIC_USER",
                "PAYA_POSTBACK_BASIC_PASSWORD",
              ]}
              secrets={conf.secrets}
              busy={busy}
              onSave={saveSecrets}
            />
            <div className="flex flex-wrap items-center gap-3">
              <Button
                size="sm"
                variant="outline"
                data-testid="payments-paya-test"
                disabled={busy || !p.paya.configured}
                onClick={testPaya}
                className="border-zinc-700 bg-transparent text-zinc-200 hover:bg-zinc-800"
              >
                <PlugsConnected size={14} weight="bold" className="mr-1.5" /> Test connection
              </Button>
              {payaTest && (
                <span
                  data-testid="payments-paya-test-result"
                  className={`text-xs ${payaTest.ok ? "text-emerald-300" : "text-amber-300"}`}
                >
                  {payaTest.ok
                    ? `Connected · ${payaTest.location} · ${payaTest.product} (${payaTest.payment_method})${
                        payaTest.active ? "" : " · product inactive"
                      } · ${payaTest.sandbox ? "sandbox" : "production"}`
                    : payaTest.error}
                </span>
              )}
            </div>
            {p.paya.missing.length > 0 && (
              <p className="text-xs text-amber-300" data-testid="payments-paya-missing">
                Missing: {p.paya.missing.join(", ")} — paste them above, or set them as backend
                environment secrets.
              </p>
            )}
          </Section>
        </div>
      </main>
    </div>
  );
}
